import { useCallback, useEffect, useRef, useState } from 'react';
import { deleteAttachment, fetchAttachments, uploadAttachment, type Attachment } from '../api';
import {
  ACEPTA,
  claseDe,
  esMultimedia,
  esVideo,
  etiquetaExtension,
  motivoDeRechazo,
  type LimitesMb,
} from './adjuntos';

/**
 * Adjuntos de una tarea: imágenes, vídeos y documentos.
 *
 * Por qué una captura y no una descripción: explicar con palabras un error en pantalla, un
 * diseño o el estado de una instalación cuesta un párrafo y se entiende a medias.
 *
 * DOS DETALLES DE LA INTERFAZ
 *
 * · Se puede pegar directamente (Ctrl+V) sobre el panel. Es como llega la mayoría de las
 *   capturas —del recorte de pantalla al portapapeles— y obligar a guardarlas antes en un
 *   fichero es un paso que nadie quiere dar.
 *
 * · El error de subida se muestra tal cual lo manda la API. El servidor ya explica cuánto
 *   pesa la imagen y cuál es el máximo; reescribirlo aquí como «error al subir» perdería
 *   justo lo que el usuario necesita para arreglarlo.
 */
export function Adjuntos({
  workItemId,
  puedeEditar,
  limites,
}: {
  workItemId: number | string;
  /** Sin permiso se ven los adjuntos pero no se sube ni se borra. */
  puedeEditar: boolean;
  /** Los techos del servidor. Se comprueban antes de subir para no gastar la subida. */
  limites: LimitesMb;
}): JSX.Element {
  const [imagenes, setImagenes] = useState<Attachment[]>([]);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ampliada, setAmpliada] = useState<Attachment | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Los tres números por separado: el objeto `limites` se crea de nuevo en cada render de
  // quien lo pasa, y como dependencia volvería a registrar el listener de pegado cada vez.
  const { imagen, video, documento } = limites;

  const recargar = useCallback(async () => {
    try {
      setImagenes(await fetchAttachments(workItemId));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los adjuntos.');
    } finally {
      setCargando(false);
    }
  }, [workItemId]);

  useEffect(() => {
    setCargando(true);
    void recargar();
  }, [recargar]);

  const subir = useCallback(
    async (file: File) => {
      // Se comprueba antes de enviar: subir 50 MB para que el servidor los rechace al
      // final gasta la conexión de quien sube y su paciencia. La comprobación del servidor
      // sigue ahí y es la que manda —esta solo evita el viaje inútil.
      const motivo = motivoDeRechazo(file, { imagen, video, documento });
      if (motivo) {
        setError(motivo);
        return;
      }
      setSubiendo(true);
      setError(null);
      try {
        await uploadAttachment(workItemId, file);
        await recargar();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo subir el adjunto.');
      } finally {
        setSubiendo(false);
      }
    },
    [workItemId, recargar, imagen, video, documento],
  );

  // Pegar una captura del portapapeles (o un fichero copiado desde el explorador).
  useEffect(() => {
    if (!puedeEditar) return;
    const alPegar = (evento: ClipboardEvent): void => {
      const archivo = Array.from(evento.clipboardData?.files ?? []).find(
        (f) => claseDe(f) !== null,
      );
      if (archivo) {
        evento.preventDefault();
        void subir(archivo);
      }
    };
    window.addEventListener('paste', alPegar);
    return () => window.removeEventListener('paste', alPegar);
  }, [puedeEditar, subir]);

  const borrar = async (id: string): Promise<void> => {
    try {
      await deleteAttachment(id);
      await recargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo quitar el adjunto.');
    }
  };

  return (
    <section className="mt-5 border-t border-[var(--color-border-subtle)] pt-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-[13px] font-semibold">
          Adjuntos {imagenes.length > 0 && `(${imagenes.length})`}
        </h3>
        {puedeEditar && (
          <button
            type="button"
            disabled={subiendo}
            onClick={() => inputRef.current?.click()}
            className="text-[12px] text-[var(--color-ink-muted)] hover:text-[var(--color-ink)] disabled:opacity-50"
          >
            {subiendo ? 'Subiendo…' : '+ Añadir'}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACEPTA}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void subir(f);
          e.target.value = ''; // permite volver a elegir el mismo fichero
        }}
      />

      {error && (
        <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          {error}
        </p>
      )}

      {cargando ? (
        <p className="mt-2 text-[12px] text-[var(--color-ink-muted)]">Cargando…</p>
      ) : imagenes.length === 0 ? (
        <p className="mt-2 text-[12px] leading-snug text-[var(--color-ink-muted)]">
          Sin adjuntos.
          {puedeEditar &&
            ' Puedes pegar una captura con Ctrl+V o usar «Añadir» para elegir una imagen, un vídeo o un documento (PDF, Office, Markdown, texto).'}
        </p>
      ) : (
        <ul className="mt-3 grid grid-cols-3 gap-2">
          {imagenes.map((img) => (
            <li key={img.id} className="group relative">
              {!esMultimedia(img.mimeType) ? (
                // Un documento no tiene miniatura: se enseña como ficha y se abre en otra
                // pestaña. PDF y texto los muestra el navegador; Office lo descarga, porque
                // así lo sirve la API (ver `cabecerasDeEntrega`).
                <a
                  href={img.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex aspect-square w-full flex-col items-center justify-center gap-1 overflow-hidden rounded-lg border border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)] p-2 text-center hover:border-[var(--color-ink-muted)]"
                  title={`${img.filename} · ${(img.bytes / 1024).toFixed(0)} KB`}
                >
                  <span className="rounded bg-[var(--color-ink)] px-1.5 text-[10px] font-semibold leading-4 text-[var(--color-surface)]">
                    {etiquetaExtension(img.filename)}
                  </span>
                  <span className="line-clamp-2 break-all text-[11px] leading-tight text-[var(--color-ink-muted)]">
                    {img.filename}
                  </span>
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => setAmpliada(img)}
                  className="block w-full overflow-hidden rounded-lg border border-[var(--color-border-subtle)]"
                  title={`${img.filename} · ${(img.bytes / 1024).toFixed(0)} KB`}
                >
                  {esVideo(img.mimeType) ? (
                    <>
                      {/* Solo los metadatos: la rejilla puede tener varios vídeos y descargarlos
                        enteros para enseñar una miniatura serían decenas de MB por abrir una
                        tarea. El primer fotograma basta para reconocer cuál es. */}
                      <video
                        src={img.url}
                        preload="metadata"
                        muted
                        className="aspect-square w-full bg-black object-cover"
                      />
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 flex items-center justify-center text-[20px] text-white/90 drop-shadow"
                      >
                        ▶
                      </span>
                    </>
                  ) : (
                    <img
                      src={img.url}
                      alt={img.filename}
                      loading="lazy"
                      className="aspect-square w-full object-cover"
                    />
                  )}
                </button>
              )}
              {puedeEditar && (
                <button
                  type="button"
                  onClick={() => void borrar(img.id)}
                  aria-label={`Quitar ${img.filename}`}
                  className="absolute right-1 top-1 hidden rounded bg-black/70 px-1.5 text-[11px] leading-5 text-white group-hover:block group-focus-within:block"
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {ampliada && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={ampliada.filename}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
          onClick={() => setAmpliada(null)}
          onKeyDown={(e) => e.key === 'Escape' && setAmpliada(null)}
          tabIndex={-1}
        >
          <figure className="max-h-full max-w-3xl">
            {esVideo(ampliada.mimeType) ? (
              // `controls` y nada de reproducción automática: quien abre un vídeo decide
              // cuándo empieza. Poder avanzar en la barra depende del `Range` que sirve la
              // API (ver `servir` en attachments.controller).
              <video
                src={ampliada.url}
                controls
                autoPlay={false}
                className="max-h-[80vh] w-auto rounded-lg bg-black"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={ampliada.url}
                alt={ampliada.filename}
                className="max-h-[80vh] w-auto rounded-lg"
              />
            )}
            <figcaption className="mt-2 text-center text-[12px] text-white/80">
              {ampliada.filename}
              {ampliada.uploadedBy && ` · subido por @${ampliada.uploadedBy}`}
              {esVideo(ampliada.mimeType) && (
                <>
                  {' · '}
                  {/* Salida para el vídeo que el navegador no sabe decodificar: un .mov de
                      iPhone reciente viene en HEVC y Chrome no lo reproduce. Sin esto, el
                      usuario ve un recuadro negro y no tiene ninguna otra opción. */}
                  <a
                    href={ampliada.url}
                    download={ampliada.filename}
                    onClick={(e) => e.stopPropagation()}
                    className="underline"
                  >
                    descargar
                  </a>
                </>
              )}
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}
